import ChalkySticks from '@chalkysticks/sdk';
import { ActionContext } from 'vuex';
import { IStoreState } from './State';

/**
 * @type ActionContext<S, R>
 */
type Context = ActionContext<IStoreState, IStoreState>;

/**
 * Actions are generally meant to be the entrypoint for modifying state.
 *
 * They are asynchronous calls that can perform other tasks and then
 * trigger a mutation (synchronous).
 *
 * @see https://vuex.vuejs.org/guide/actions.html
 *
 * @class Actions
 * @package Store/Module/Authentication
 * @project ChalkySticks App
 */
const actions = {
	/**
	 * @param Context context
	 * @return void
	 */
	login(context: Context, payload: IAuthResponse): void {
		context.commit('setToken', payload.token);
		context.commit('setUser', payload.user);

		ChalkySticks.Core.Event.Bus.dispatch('authentication:login', {
			token: payload.token,
			user: payload.user,
		});

		ChalkySticks.Utility.Analytics.setGlobalProps((prev: any) => ({
			...prev,
			user: payload.user.id,
		}));

		ChalkySticks.Utility.Analytics.track('authentication:login');
	},

	/**
	 * @param Context context
	 * @return void
	 */
	logout(context: Context): void {
		ChalkySticks.Utility.Analytics.track('authentication:logout');

		context.commit('logout');

		ChalkySticks.Utility.Analytics.setGlobalProps((prev: any) => ({
			...prev,
			user: 'guest',
		}));
	},

	/**
	 * Refresh the user model from the API.
	 *
	 * @param Context context
	 * @return Promise<void>
	 */
	async refreshUser(context: Context): Promise<void> {
		try {
			const userModel = context.getters.user;

			// Fetch new data
			await userModel?.fetch();

			// Couldn't find a user for some reason
			if (!userModel?.id) {
				return;
			}

			// Update locally
			actions.updateUser(context, userModel);
		} catch (e: any) {
			console.warn('Could not refresh user', e);
		}
	},

	/**
	 * @param Context context
	 * @param ChalkySticks.Model.User userModel
	 * @return void
	 */
	updateUser(context: Context, userModel?: ChalkySticks.Model.User): void {
		const json = userModel ? userModel.toJSON() : context.state.user;

		context.commit('updateUser', json);
	},
};

export default actions;
